import { OktaAuth, AccessToken } from '@okta/okta-auth-js';
import { ConfigService } from '$State/ConfigFreezerService';
import { AuthProviderToken, IAuthProvider } from './IAuthProvider';

export class OktaAuthProvider implements IAuthProvider {
    private oktaAuth: OktaAuth;

    constructor() {
        const baseUrl = ConfigService.getBaseUrl();

        this.oktaAuth = new OktaAuth({
            issuer: ConfigService.getOktaIssuerUrl(),
            clientId: ConfigService.getOktaClientId(),
            redirectUri: baseUrl + '/loginResponse',
            postLogoutRedirectUri: baseUrl + '/logoutResponse',
            scopes: ['enc.api', 'openid', 'profile', 'email', 'offline_access'],
            tokenManager: { autoRenew: true, autoRemove: false },
        });

        if (!this.oktaAuth.token.isLoginRedirect()) {
            this.oktaAuth.start();
        }
    }

    public login(): Promise<void> {
        return this.oktaAuth.signInWithRedirect();
    }

    public async getUser(): Promise<AuthProviderToken | null> {
        const { accessToken } = await this.oktaAuth.tokenManager.getTokens();
        return accessToken ? this.toAuthProviderToken(accessToken) : null;
    }

    public async loginCallback(): Promise<void | AuthProviderToken> {
        if (this.oktaAuth.token.isLoginRedirect()) {
            const { tokens } = await this.oktaAuth.token.parseFromUrl();
            this.oktaAuth.tokenManager.setTokens(tokens);
            this.oktaAuth.start();
        }

        return (await this.getUser()) ?? undefined;
    }

    public async renewToken(): Promise<AuthProviderToken | null> {
        const renewed = await this.oktaAuth.tokenManager.renew('accessToken');
        return renewed ? this.toAuthProviderToken(renewed as AccessToken) : null;
    }

    public async logout(): Promise<void> {
        await this.oktaAuth.signOut({ clearTokensBeforeRedirect: true });
    }

    public async logoutCallback(): Promise<void> {
        this.oktaAuth.tokenManager.clear();
    }

    public async dispose(): Promise<void> {
        await this.oktaAuth.stop();
    }

    private toAuthProviderToken(accessToken: AccessToken): AuthProviderToken {
        const nowInSeconds = Math.floor(Date.now() / 1000);
        return {
            access_token: accessToken.accessToken,
            expires_in: accessToken.expiresAt - nowInSeconds,
            expired: accessToken.expiresAt <= nowInSeconds,
        };
    }
}
